import { getSessionId } from "@bodhistreams/utils";
import axios from "axios";
import axiosInstance from "./axiosInstanceV1";

/** `X-Session-Id` header when a session id exists, so backend `auth` telemetry
 *  can be joined to the frontend events from the same tab. */
const sessionHeader = () => {
	const sid = getSessionId();
	return sid ? { "X-Session-Id": sid } : {};
};

// ========== Public auth endpoints (no JWT required) ==========

/**
 * Log in with email and password
 * @param {string} email
 * @param {string} password
 * @returns {Promise<Object>} { token, refresh_token, user }
 */
export const login = async (email, password) => {
	const response = await axiosInstance.post("/public/auth/login", {
		email,
		password,
	});
	return response.data;
};

/**
 * Register a new account
 * @param {Object} userData
 * @returns {Promise<Object>} Response data
 */
export const register = async (userData) => {
	const response = await axiosInstance.post("/public/auth/register", userData);
	return response.data;
};

/**
 * Refresh an access token using a refresh token (via cookie or JSON body)
 *
 * `reason` names the caller and is echoed into the backend's `auth` telemetry
 * as `X-Refresh-Reason`. Always pass one: when a session resurrects itself, the
 * reason is what tells you *which* code path re-minted it.
 * @param {string} token
 * @param {"interceptor"|"ws_reconnect"|"explicit"} [reason]
 * @returns {Promise<Object>} { token, refresh_token }
 */
export const refreshToken = async (token, reason = "explicit") => {
	const baseURL = import.meta.env.DEV
		? "/api/v1"
		: "https://api.bodhistreams.com";
	// When no token provided, send empty body so server falls back to the cookie.
	// Sending `{ refresh_token: "" }` would short-circuit the cookie fallback.
	const body = token ? { refresh_token: token } : {};
	const response = await axios.post(`${baseURL}/public/auth/refresh`, body, {
		withCredentials: true,
		headers: {
			"X-Refresh-Reason": reason,
			...sessionHeader(),
		},
	});
	return response.data;
};

/**
 * Logout — revokes the refresh token and clears auth cookies server-side
 * @returns {Promise<Object>} { success, message }
 */
export const logout = async () => {
	const response = await axiosInstance.post("/public/auth/logout", null, {
		headers: sessionHeader(),
	});
	return response.data;
};

/**
 * Verify email address with a verification token
 * @param {string} token
 * @returns {Promise<Object>} Response data
 */
export const verifyEmail = async (token) => {
	const response = await axiosInstance.post("/public/auth/verify-email", {
		token,
	});
	return response.data;
};

/**
 * Resend a verification email
 * @param {string} userId
 * @returns {Promise<Object>} Response data
 */
export const resendVerificationEmail = async (userId) => {
	const response = await axiosInstance.post(
		`/public/auth/resend-verification/${userId}`,
	);
	return response.data;
};

/**
 * Resend a verification email by address. Used by the sign-in screen after the
 * server answers EMAIL_NOT_VERIFIED, when no user id is known client-side.
 * @param {string} email
 * @returns {Promise<Object>} Response data
 */
export const resendVerificationByEmail = async (email) => {
	const response = await axiosInstance.post(
		"/public/auth/resend-verification",
		{ email },
	);
	return response.data;
};

/**
 * Login or register with a Google OAuth code (web)
 * @param {string} code - OAuth authorization code
 * @returns {Promise<Object>} { token, refresh_token, user }
 */
export const googleLogin = async (code) => {
	const response = await axiosInstance.post("/public/auth/google", { code });
	return response.data;
};

/**
 * Login or register with a Google ID token (mobile)
 * @param {string} idToken - Google ID token
 * @returns {Promise<Object>} { token, refresh_token, user }
 */
export const googleMobileLogin = async (idToken) => {
	const response = await axiosInstance.post("/public/auth/google/mobile", {
		id_token: idToken,
	});
	return response.data;
};

/**
 * Reset password using a reset token
 * @param {string} token - Password reset token
 * @param {string} newPassword
 * @returns {Promise<Object>} Response data
 */
export const resetPassword = async (token, newPassword) => {
	const response = await axiosInstance.post("/public/auth/reset-password", {
		token,
		new_password: newPassword,
	});
	return response.data;
};

/**
 * Request a password reset email
 * @param {string} email
 * @returns {Promise<Object>} Response data
 */
export const requestPasswordReset = async (email) => {
	const response = await axiosInstance.post(
		"/public/auth/request-password-reset",
		{ email },
	);
	return response.data;
};

// ========== User auth endpoints (JWT required) ==========

/**
 * Fetch current user identity from JWT claims (lightweight, no DB query)
 * @returns {Promise<Object>} { user_id, email, first_name, last_name, is_admin, membership_name, membership_id }
 */
export const fetchAuthMe = async () => {
	const response = await axiosInstance.get("/user/auth/me");
	return response.data;
};

/**
 * Validate the current access token
 * @returns {Promise<Object>} { valid, email, user_id, is_admin, ... }
 */
export const validateToken = async () => {
	const response = await axiosInstance.get("/user/auth/validate");
	return response.data;
};
